import Link from "next/link";
import { ArrowTopRightOnSquareIcon as ExternalLinkIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "@/components/ui/use-toast";
import Meta from "../components/meta";
import LocationDetails from "../components/location-details";
import { getSettingsForPage } from "../lib/api";

type FormData = {
  name: string;
  email: string;
  phone?: string;
  message: string;
  website?: string; // Honeypot field
};

export default function Contact({ data }: { data: any }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>();

  const onSubmit = async (formData: FormData) => {
    try {
      setIsSubmitting(true);
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error("Failed to submit form");
      }

      setIsSuccess(true);
      reset();
      toast({
        title: "Success",
        description: "Message sent successfully!",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)] bg-white px-6">
        <div className="text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Thank you for your message!
          </h2>
          <p className="mt-4 text-lg text-gray-500">
            We&apos;ve received your contact request and will get back to you
            soon.
          </p>
          <button
            onClick={() => setIsSuccess(false)}
            className="mt-8 rounded-md bg-accent-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-accent-500"
          >
            Send another message
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Meta path="/contact" {...data.seo} />
      <div className="relative isolate bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-2 ">
          <div className="relative px-6 pb-20 pt-24 sm:pt-32 lg:static lg:px-8 lg:py-48">
            <div className="mx-auto max-w-xl lg:mx-0 lg:max-w-xl lg:mr-4">
              <div className="absolute inset-y-0 left-0 -z-10 w-full overflow-hidden bg-gray-100 lg:w-1/2"></div>
              <h2 className="text-pretty text-4xl font-semibold tracking-tight text-gray-900 sm:text-5xl">
                {data.hero.title}
              </h2>
              <p className="mt-6 text-lg/8 text-gray-600">
                {data.hero.description}
              </p>

              <div className="mt-10 max-w-lg mx-auto md:max-w-none md:grid md:grid-cols-2 md:gap-2">
                {data.locations.map((l: any, index: any) => (
                  <LocationDetails
                    titleClassName="text-lg font-medium text-gray-600"
                    titleMarginTop="mt-6"
                    textClassName="ml-3 text-base text-gray-700"
                    key={l._key}
                    data={l}
                    className={index > 0 ? "mt-12 sm:mt-16 md:mt-0" : ""}
                  />
                ))}
              </div>
            </div>
          </div>

          <form
            onSubmit={handleSubmit(onSubmit)}
            className="px-6 pb-24 pt-20 sm:pb-32 lg:px-8 lg:py-48"
          >
            <div className="mx-auto max-w-xl lg:mr-0 lg:max-w-lg">
              {/* Honeypot field - hidden from users, bots will fill it */}
              <input
                {...register("website")}
                type="text"
                name="website"
                autoComplete="off"
                tabIndex={-1}
                className="absolute left-[-9999px]"
                aria-hidden="true"
              />

              <div className="grid grid-cols-1 gap-x-8 gap-y-6">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm/6 font-semibold text-gray-900"
                  >
                    Name *
                  </label>
                  <div className="mt-2.5">
                    <input
                      {...register("name", { required: "Name is required" })}
                      type="text"
                      className="block w-full rounded-md bg-white px-3.5 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-accent-600 focus:ring-accent-600"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.name.message}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm/6 font-semibold text-gray-900"
                  >
                    Email *
                  </label>
                  <div className="mt-2.5">
                    <input
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "Invalid email address",
                        },
                      })}
                      type="email"
                      className="block w-full rounded-md bg-white px-3.5 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-accent-600 focus:ring-accent-600"
                    />
                    {errors.email && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.email.message}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm/6 font-semibold text-gray-900"
                  >
                    Phone number (optional)
                  </label>
                  <div className="mt-2.5">
                    <input
                      {...register("phone")}
                      type="tel"
                      className="block w-full rounded-md bg-white px-3.5 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-accent-600 focus:ring-accent-600"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm/6 font-semibold text-gray-900"
                  >
                    Message *
                  </label>
                  <div className="mt-2.5">
                    <textarea
                      {...register("message", {
                        required: "Message is required",
                      })}
                      rows={4}
                      className="block w-full rounded-md bg-white px-3.5 py-2 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-accent-600 focus:ring-accent-600"
                    />
                    {errors.message && (
                      <p className="mt-1 text-sm text-red-600">
                        {errors.message.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-8 flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="rounded-md bg-accent-600 px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-accent-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Sending..." : "Send message"}
                </button>
              </div>
            </div>
          </form>
        </div>

        <div className="bg-accent-700">
          <div className="max-w-md mx-auto text-center py-16 px-4 sm:max-w-2xl sm:py-24 sm:px-6 lg:px-8 lg:py-32">
            <h2 className="text-3xl font-extrabold sm:text-4xl">
              <span className="block text-white">{data.careers.title}</span>
              <span className="block text-accent-200">
                {data.careers.subtitle}
              </span>
            </h2>
            <Link
              href="/careers"
              className="mt-8 w-full inline-flex items-center justify-center py-3 px-5 bg-white border border-transparent rounded-md shadow-md text-base font-medium text-accent-600 hover:bg-accent-50 sm:w-auto"
            >
              <span>{data.careers.button}</span>
              <ExternalLinkIcon
                className="ml-3 h-5 w-5 flex-shrink-0 text-gray-400"
                aria-hidden="true"
              />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

export async function getStaticProps() {
  const data = await getSettingsForPage("contact");

  return {
    props: { data },
    revalidate: 10,
  };
}
